import * as React from 'react';

import {
  CurrencyCode,
  enumInvoiceType,
  enumPaymentTerm,
  InvoiceType,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  ConsoleDialog,
  DatePicker,
  Row,
  TextArea,
  TextField,
  type DateValue,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as formState from '../../_state/invoiceForm.state';
import {
  FinanceLineItemData,
  FinanceLineItemTable,
} from '../_shared/FinanceLineItemTable';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { StepIndicator } from '~/modules/trade/_components/_shared/StepIndicator';
import { toMoneyInput } from '~/modules/trade/utils/money';
import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { anyCalendarDateToCalendarDate } from '~/shared/utils/dateTime';

type InvoiceWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

interface Step {
  number: number;
  title: string;
}

const getWizardSteps = (t: ReturnType<typeof useTranslations>): Step[] => [
  { number: 1, title: t.invoices.wizard.steps.basicInfo },
  { number: 2, title: t.invoices.wizard.steps.lineItems },
];

export const InvoiceWizard = ({
  isOpen,
  onClose,
  onSuccess,
}: InvoiceWizardProps) => {
  const toast = useToast();
  const t = useTranslations();
  const wizardSteps = React.useMemo(() => getWizardSteps(t), [t]);

  const [form] = useAtom(formState.$invoiceForm);
  const [currentStep, setCurrentStep] = useAtom(formState.$currentStep);
  const [isSubmitting, setIsSubmitting] = useAtom(formState.$isSubmitting);
  const updateForm = useSetAtom(formState.$updateInvoiceForm);
  const resetForm = useSetAtom(formState.$resetInvoiceForm);
  const validateForm = useSetAtom(formState.$validateInvoiceForm);
  const createInvoice = useSetAtom(formState.$createInvoice);
  const isMissingRequired = useAtomValue(formState.$invoiceIsMissingRequired);
  const nextDocumentNumberMutation = useSetAtom(
    formState.$nextInvoiceDocumentNumber,
  );
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === wizardSteps.length;

  React.useEffect(() => {
    if (!isOpen || form.id || form.documentNumber) return;

    const abortController = new AbortController();
    nextDocumentNumberMutation({})
      .then((data) => {
        if (abortController.signal.aborted) return;
        updateForm({
          documentNumber: data.financeNextInvoiceDocumentNumber,
        });
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        console.error('Failed to get next invoice document number:', error);
      });
    return () => {
      abortController.abort();
    };
  }, [
    isOpen,
    form.id,
    form.documentNumber,
    nextDocumentNumberMutation,
    updateForm,
  ]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleNext = React.useCallback(() => {
    const validation = validateForm();
    if (validation.isValid && currentStep < wizardSteps.length) {
      setCurrentStep(currentStep + 1);
    } else if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(', ');
      toast.error(errorMessages);
    }
  }, [validateForm, currentStep, setCurrentStep, toast]);

  const handleBack = React.useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, setCurrentStep]);

  const handleSubmit = React.useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(', ');
      toast.error(errorMessages);
      return;
    }

    try {
      setIsSubmitting(true);

      const totalAmount = form.lineItems.reduce(
        (sum, li) => sum + (li.totalPrice?.amount ?? 0),
        0,
      );

      if (
        !form.facilityId ||
        !form.accountId ||
        !form.documentDate ||
        !form.paymentTerm
      )
        return;

      await createInvoice({
        type: form.type,
        documentNumber: form.documentNumber || undefined,
        facilityId: form.facilityId,
        accountId: form.accountId,
        documentDate: form.documentDate,
        paymentTerm: form.paymentTerm,
        amount: { amount: totalAmount, currencyCode: form.currencyCode },
        notes: form.notes ?? undefined,
        lineItems: form.lineItems.map((li) => ({
          description: li.description,
          quantity: li.quantity ?? 0,
          unit: li.unit ?? '',
          unitPrice: toMoneyInput(li.unitPrice),
          totalPrice: toMoneyInput(li.totalPrice),
          itemId: li.itemId ?? undefined,
        })),
      });

      toast.success(t.invoices.wizard.messages.createSuccess);
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(
        t.invoices.wizard.messages.createError(
          error instanceof Error
            ? error.message
            : t.messages.common.unknownError,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateForm,
    toast,
    setIsSubmitting,
    createInvoice,
    form,
    onSuccess,
    handleClose,
    t,
  ]);

  const handleUpdateLineItem = React.useCallback(
    (id: string, updates: Partial<FinanceLineItemData>) => {
      updateForm({
        lineItems: form.lineItems.map((li) =>
          li.id === id ? { ...li, ...updates } : li,
        ),
      });
    },
    [form.lineItems, updateForm],
  );

  const handleDeleteLineItem = React.useCallback(
    (id: string) => {
      updateForm({
        lineItems: form.lineItems.filter((li) => li.id !== id),
      });
    },
    [form.lineItems, updateForm],
  );

  const handleAddLineItem = React.useCallback(() => {
    const maxSortOrder = form.lineItems.reduce(
      (max, item) => Math.max(max, item.sortOrder),
      -1,
    );
    const newItem: FinanceLineItemData = {
      id: `temp-${Date.now()}-${Math.random()}`,
      description: '',
      quantity: null,
      unit: null,
      unitPrice: null,
      totalPrice: null,
      itemId: null,
      groupName: null,
      sortOrder: maxSortOrder + 1,
    };
    updateForm({
      lineItems: [...form.lineItems, { ...newItem, isNew: true }],
    });
  }, [form.lineItems, updateForm]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="grid grid-cols-2 gap-4 p-4">
            <TextField
              label={t.invoices.wizard.fields.documentNumber}
              value={form.documentNumber ?? ''}
              onChange={(value) =>
                updateForm({ documentNumber: value || undefined })
              }
              placeholder={t.common.placeholders.generateNumber}
            />
            <SelectEnum<InvoiceType>
              label={t.invoices.wizard.fields.invoiceType}
              enumValue={enumInvoiceType}
              value={form.type}
              onChange={(value) => updateForm({ type: value })}
              formatOptionLabel={(value) => t.enums.invoiceType[value] ?? value}
              isRequired
            />
            <SelectFacility
              label={t.invoices.wizard.fields.facility}
              value={form.facilityId ?? undefined}
              onChange={(value) => updateForm({ facilityId: value ?? null })}
              isRequired
            />
            <SelectAccount
              label={t.invoices.wizard.fields.account}
              value={form.accountId ?? undefined}
              onChange={(value) => updateForm({ accountId: value ?? null })}
              isRequired
            />
            <DatePicker
              label={t.invoices.wizard.fields.documentDate}
              value={form.documentDate as DateValue | undefined}
              onChange={(value) =>
                updateForm({
                  documentDate: anyCalendarDateToCalendarDate(
                    value ?? undefined,
                  ),
                })
              }
              isRequired
            />
            <DatePicker
              label={t.invoices.wizard.fields.dueDate}
              value={form.dueDate as DateValue | undefined}
              onChange={(value) =>
                updateForm({
                  dueDate: anyCalendarDateToCalendarDate(value ?? undefined),
                })
              }
            />
            <SelectEnum<PaymentTerm>
              label={t.invoices.wizard.fields.paymentTerm}
              enumValue={enumPaymentTerm}
              value={form.paymentTerm ?? undefined}
              onChange={(value) => updateForm({ paymentTerm: value ?? null })}
              isRequired
            />
            <SelectCurrency
              label={t.invoices.wizard.fields.currency}
              value={form.currencyCode}
              onChange={(value) =>
                updateForm({ currencyCode: value as CurrencyCode })
              }
              isRequired
            />
            <div className="col-span-2">
              <TextArea
                label={t.invoices.wizard.fields.notes}
                value={form.notes ?? ''}
                onChange={(value) => updateForm({ notes: value || null })}
                rows={3}
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="flex flex-col gap-4 p-4">
            <h3 className="text-lg font-medium">
              {t.invoices.lineItems.title}
            </h3>
            <FinanceLineItemTable
              lineItems={form.lineItems}
              onUpdateLineItem={handleUpdateLineItem}
              onDeleteLineItem={handleDeleteLineItem}
              onAddLineItem={handleAddLineItem}
              currencyCode={form.currencyCode}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <ConsoleDialog
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@InvoiceWizard"
      title={
        <Col gap3>
          <Row gap3 className="text-xl font-semibold">
            <span>{t.invoices.list.buttons.create}</span>
          </Row>
          <StepIndicator steps={wizardSteps} currentStep={currentStep} />
        </Col>
      }
      titleClassName="bg-foreground/5 pt-3"
      content={renderStepContent()}
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <div>
            {!isFirstStep && (
              <Button
                color="neutral"
                variant="outline"
                onPress={handleBack}
                isPending={isSubmitting}
              >
                {t.invoices.wizard.buttons.back}
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              color="neutral"
              variant="outline"
              onPress={handleClose}
              isPending={isSubmitting}
            >
              {t.invoices.wizard.buttons.cancel}
            </Button>
            {isLastStep ? (
              <Button
                color="primary"
                variant="solid"
                onPress={handleSubmit}
                isPending={isSubmitting}
                isLoading={isSubmitting}
                isDisabled={isMissingRequired}
              >
                {t.invoices.wizard.buttons.create}
              </Button>
            ) : (
              <Button
                color="primary"
                variant="solid"
                onPress={handleNext}
                isPending={isSubmitting}
              >
                {t.invoices.wizard.buttons.next}
              </Button>
            )}
          </div>
        </div>
      }
    />
  );
};
